import 'app/Window'

import { config, IConfig } from 'app/config'
import { ActivityFeedService } from 'features/ActivityFeed'
import {
  createQuoteSheetEndpoint,
  HtmlPdfService,
  QuoteServiceDeprecated,
  QuoteSheetEndpoint,
} from 'features/BookingFlow'
import { BookingRequestService } from 'features/BookingRequests'
import { BookingTemplateService } from 'features/BookingTemplates'
import { NetworkService, PlaceService, VatService } from 'features/Directory'
import {
  createManualQuotesListEndpoint,
  createSeaportsEndpoint,
  ManualQuotesListEndpoint,
  ManualQuoteStore,
  SeaportsEndpoint,
} from 'features/ManualQuotes'
import { ReportService } from 'features/Reports'
import {
  AddressService,
  createShipmentEndpoint,
  RequiredActionsService,
  ShipmentEndpoint,
} from 'features/ShipmentDetails'
import { SearchService } from 'features/ShipmentsList'
import { CountryOptionsProvider, IsoCountryService, SignupService } from 'features/Signup'
import { TransportPlanService } from 'features/TransportPlan'
import { History } from 'history'
import { observe, toJS } from 'mobx'
import { EventBus, initI18next, LocalisationStore } from 'utils/localisation'
import { logError } from 'utils/logger'

import { AnalyticsService } from './AnalyticsService'
import { API } from './API'
import { AuthStore } from './Auth'
import { ErrorService } from './ErrorService'
import { FAQService } from './FAQService'
import { createHttpBaseApiWrapper, HttpBaseApiWrapper } from './HttpBaseApiWrapper'
import { AnalyticsStore } from './internal/AnalyticsStore'
import { NotificationToastService } from './internal/NotificationToastService'
import { UserlaneService } from './internal/UserlaneService'
import { SocketManager } from './SocketService'
import { UIStore } from './UIStore'
import { getAPIBaseUrl } from './url'

interface RootStoreProps {
  history: History
  analyticsService: AnalyticsService
}
export class RootStore {
  config: IConfig = config
  history: History
  uiStore: UIStore

  http: HttpBaseApiWrapper
  API: API
  errorService: ErrorService
  faqService: FAQService
  analyticsStore: AnalyticsStore

  shipmentEndpoint: ShipmentEndpoint
  quoteSheetEndpoint: QuoteSheetEndpoint
  manualQuotesListEndpoint: ManualQuotesListEndpoint
  seaportsEndpoint: SeaportsEndpoint
  transportPlanService: TransportPlanService
  requiredActionsService: RequiredActionsService
  placeService: PlaceService
  searchService: SearchService

  addressService: AddressService
  authStore: AuthStore
  localisationStore: LocalisationStore
  manualQuoteStore: ManualQuoteStore
  quoteService: QuoteServiceDeprecated
  activityFeedService: ActivityFeedService
  htmlPdfService: HtmlPdfService

  userlaneService: UserlaneService
  reportService: ReportService
  socketManager: SocketManager
  analyticsService: AnalyticsService

  signupService: SignupService
  networkService: NetworkService
  vatService: VatService
  isoCountryService: IsoCountryService
  countryOptionsProvider: CountryOptionsProvider
  bookingTemplateService: BookingTemplateService
  bookingRequestService: BookingRequestService
  notificationToastService: NotificationToastService
  eventBus: EventBus

  constructor(props: RootStoreProps) {
    this.eventBus = new EventBus()
    this.history = props.history
    this.uiStore = new UIStore()
    this.localisationStore = new LocalisationStore({
      getClient: () => initI18next(getAPIBaseUrl),
      eventBus: this.eventBus,
    })
    this.errorService = new ErrorService({
      dialog: this.uiStore.dialog,
      localisationStore: this.localisationStore,
      log: logError,
      history: this.history,
    })
    this.faqService = new FAQService()

    this.http = createHttpBaseApiWrapper(
      () => this.errorService.showGeneralError(),
      () => this.authStore,
      () => this.uiStore
    )
    this.API = new API({
      http: this.http,
    })
    this.shipmentEndpoint = createShipmentEndpoint(this.http)
    this.quoteSheetEndpoint = createQuoteSheetEndpoint(this.http)
    this.seaportsEndpoint = createSeaportsEndpoint(this.http)
    this.manualQuotesListEndpoint = createManualQuotesListEndpoint(this.http)
    this.transportPlanService = new TransportPlanService(this.http)
    this.requiredActionsService = new RequiredActionsService(this.http)
    this.placeService = new PlaceService(this.http)
    this.searchService = new SearchService(this.http)
    this.reportService = new ReportService(this.http)
    this.socketManager = new SocketManager()
    this.addressService = new AddressService({ httpAPI: this.http })
    this.authStore = new AuthStore({
      API: this.API,
      history: this.history,
      errorService: this.errorService,
      storage: localStorage,
    })
    this.authStore.init()
    this.manualQuoteStore = new ManualQuoteStore(this.API, this.history, this.authStore)

    this.quoteService = new QuoteServiceDeprecated({
      http: this.http,
      uiStore: this.uiStore,
      manualQuoteStore: this.manualQuoteStore,
      errorService: this.errorService,
      localisationStore: this.localisationStore,
      history: this.history,
    })

    this.activityFeedService = new ActivityFeedService({
      API: this.API,
    })

    this.htmlPdfService = new HtmlPdfService()

    this.analyticsService = props.analyticsService
    this.analyticsStore = new AnalyticsStore(this.API)
    this.isoCountryService = new IsoCountryService({
      localisationStore: this.localisationStore,
    })

    this.networkService = new NetworkService(this.API)
    this.vatService = new VatService(this.API)
    this.signupService = new SignupService({ API: this.API })
    this.countryOptionsProvider = new CountryOptionsProvider(this.isoCountryService)
    this.bookingTemplateService = new BookingTemplateService(this.API)
    this.bookingRequestService = new BookingRequestService(this.API)
    this.notificationToastService = new NotificationToastService()
    this.userlaneService = new UserlaneService(
      config.USER_LANE_APP_ID,
      config.APP_ENV,
      this.authStore,
      this.analyticsService
    )

    this.initApp()
  }

  initApp() {
    observe(this.authStore.state, 'customerLoaded', (change) => {
      const customerLoaded = change.newValue
      if (customerLoaded) {
        const { state } = this.authStore
        const { customer, user, loggedInAsCustomer, shipmentMetadata, tokenRequestedFrom } = state

        this.localisationStore.setLanguage(user.preferredLanguage)

        window.dataLayer.push({
          language: user.preferredLanguage,
          userId: user._id,
          customerId: user.customer_id,
          shipmentsBookedByUser: shipmentMetadata.bookedByUser,
          shipmentsVisibleToUser: shipmentMetadata.visibleForUser,
          roles: toJS(user.roles),
          segment: customer.segment || null,
          isCustomerLogin: loggedInAsCustomer,
          isDemo: customer.isDemo,
        })

        if (this.socketManager.shouldLoadSocket()) {
          this.socketManager.initSocket(customer._id, state.socketUserToken, 'ship')
        }
        this.userlaneService.init()
        this.analyticsStore.addTrackingListeners()
        this.analyticsService.init(customer, user, loggedInAsCustomer, tokenRequestedFrom)
      } else {
        this.socketManager.disconnectAll()
        this.analyticsStore.removeTrackingListeners()
        this.userlaneService.hide()
      }
    })

    observe(this.authStore.state, 'authenticated', () => {
      if (!this.authStore.isAuthenticated()) {
        this.analyticsService.reset()
      }
    })

    observe(this.localisationStore, 'current', () => {
      const { customer, user } = this.authStore.state
      const languageCode = this.localisationStore.current
      if (user && user.preferredLanguage !== languageCode) {
        window.dataLayer.push({ language: languageCode })
        this.API.customers.updateUser(customer._id, user._id, {
          preferredLanguage: languageCode,
        })
        user.preferredLanguage = languageCode
      }
    })
  }
}
